import { Preferences } from '@capacitor/preferences'
import { BrandingExperience, BrandingTheme } from '@torticity/types'

import { DEFAULT_BRANDING, DEFAULT_THEME } from '../config/branding'

export default defineNuxtRouteMiddleware(async to => {
  const { $http, $sentry } = useNuxtApp()

  const brand = useBrand()
  const { setTheme } = useTheme()
  const auth = useAuth()

  if (auth?.user) {
    $sentry.setUser(auth.user)
  }

  const dockitApiUrl = useRuntimeConfig().public.dockitApiUrl
  const cookieSubdomain = useCookie('subdomain')

  const isNative = useIsNative()

  if (isNative) {
    if (auth?.user) {
      try {
        const leadBranding = await $http.$get(`/v1/branding`)
        brand.value = leadBranding
        setTheme(leadBranding?.theme)

        await Preferences.set({
          key: 'subdomain',
          value: leadBranding?.subdomain,
        })

        if (leadBranding?.subdomain) {
          $sentry.setTag('subdomain', leadBranding.subdomain)
        }
      } catch (error) {
        $sentry.captureException(error)
        console.error('Failed to fetch lead branding:', error)
      }
      return
    }

    try {
      const { value: storedSubdomain } = await Preferences.get({ key: 'subdomain' })

      if (storedSubdomain) {
        try {
          const branding = await $http.$get(
            `${dockitApiUrl}/v1/branding/subdomain/${storedSubdomain}`,
          )

          brand.value = branding
          setTheme(branding?.theme)

          return
        } catch (error) {
          $sentry.captureException(error)
          console.error('Failed to fetch subdomain branding:', error)
        }
      }
    } catch (error) {
      $sentry.captureException(error)
      console.error('Failed to get subdomain from Preferences:', error)
    }

    // Fallback to default if we couldn't get stored subdomain or fetch branding from lead
    brand.value = DEFAULT_BRANDING
    setTheme(DEFAULT_THEME)
    return
  }

  const subdomain =
    useRuntimeConfig().public.subdomain ||
    cookieSubdomain?.value ||
    to.query?.brand ||
    window?.location?.hostname?.split('.')?.[0] ||
    ''

  if (subdomain) {
    $sentry.setTag('subdomain', subdomain as string)
  }

  try {
    brand.value = await $http.$get(`${dockitApiUrl}/v1/branding/subdomain/${subdomain}`)
    setTheme((brand?.value?.theme as BrandingTheme) || 'violet')
  } catch (error: any) {
    $sentry.captureException(error)
    brand.value = DEFAULT_BRANDING
    setTheme(DEFAULT_THEME)
  }

  const links =
    brand.value.brandingExperience === BrandingExperience.COBRAND
      ? [
          {
            rel: 'icon',
            href: '/favicon.ico',
          },
          {
            rel: 'icon',
            sizes: 'any',
            type: 'image/svg+xml',
            href: '/favicon.svg',
          },
        ]
      : [
          {
            rel: 'apple-touch-icon',
            type: 'image/png',
            sizes: '180x180',
            href: brand.value.faviconUrl + 'apple-touch-icon.png',
          },
          {
            rel: 'icon',
            href: brand.value.faviconUrl + 'favicon.ico',
          },
          {
            rel: 'mask-icon',
            href: brand.value.faviconUrl + 'favicon.ico',
          },
          {
            rel: 'shortcut icon',
            href: brand.value.faviconUrl + 'favicon.ico',
          },
          {
            rel: 'alternate icon',
            href: brand.value.faviconUrl + 'favicon.ico',
          },
          {
            rel: 'icon',
            type: 'image/png',
            sizes: '32x32',
            href: brand.value.faviconUrl + 'favicon-32x32.png',
          },
          {
            rel: 'icon',
            type: 'image/png',
            sizes: '16x16',
            href: brand.value.faviconUrl + 'favicon-16x16.png',
          },
          {
            rel: 'manifest',
            href: brand.value.faviconUrl + 'site.webmanifest',
          },
        ]

  useHead({
    title: 'CaseWise',
    link: links,
  })
})
